import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDueDiligence } from "../../../Common/Hooks/useDueDiligence";
import styled from "styled-components";

import { FiCheckCircle, FiAlertCircle, FiLoader, FiArrowRight } from "react-icons/fi";
import { CaseDetailsWrapper, CaseDetailsContent } from "../CaseDetails/Styles";

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 4rem 2rem;
  text-align: center;
`;

const SuccessCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
  width: 100%;
`;

const IconContainer = styled.div<{ $status: 'success' | 'error' | 'loading' }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;

  background-color: ${props => {
    switch(props.$status) {
      case 'success': return '#DCFCE7';
      case 'error': return '#FEE2E2';
      case 'loading': return '#E0F2FE';
      default: return '#E0F2FE';
    }
  }};

  color: ${props => {
    switch(props.$status) {
      case 'success': return '#16A34A';
      case 'error': return '#DC2626';
      case 'loading': return '#0284C7';
      default: return '#0284C7';
    }
  }};

  svg {
    width: 40px;
    height: 40px;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1rem 0;
  letter-spacing: -0.025em;
`;

const Message = styled.p`
  font-size: 1.125rem;
  color: #6B7280;
  line-height: 1.6;
  margin: 0 0 2.5rem 0;
  max-width: 480px;
`;

const MessageGroup = styled.div`
  max-width: 480px;
  margin: 0 0 2.5rem 0;

  ${Message} {
    margin: 0 0 1rem 0;
    max-width: none;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Button = styled.button`
  background-color: #000000;
  color: #FFFFFF;
  border: none;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

  &:hover {
    background-color: #1a1a1a;
    transform: translateY(-1px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: #6B7280;
  border: none;
  padding: 0.75rem 1.5rem;
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }
`;

export const PaymentSuccess = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const caseAlias = searchParams.get("caseAlias") ?? searchParams.get("caseId");
  const sessionId = searchParams.get("session_id");
  const { verifyPayment } = useDueDiligence();
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verify = async () => {
      if (!caseAlias || !sessionId) {
        setVerifying(false);
        return;
      }

      try {
        const result: any = await verifyPayment(caseAlias, sessionId);
        if (result && result.isPaid) {
             // verified
        } else {
            console.warn("Payment verification returned false or failed", result);
        }
      } catch (err) {
        console.error("Verification error", err);
        setError(t("dueDiligence.paymentSuccess.error.genericMessage"));
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [caseAlias, sessionId, t]);

  if (verifying) {
      return (
        <CaseDetailsWrapper>
          <CaseDetailsContent style={{ minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SuccessContainer>
                <IconContainer $status="loading">
                    <FiLoader className="spin" style={{ animation: 'spin 1.5s linear infinite' }} />
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                </IconContainer>
                <Title>{t("dueDiligence.paymentSuccess.verifying.title")}</Title>
                <Message>{t("dueDiligence.paymentSuccess.verifying.message")}</Message>
            </SuccessContainer>
          </CaseDetailsContent>
        </CaseDetailsWrapper>
      );
  }

  if (error) {
     return (
        <CaseDetailsWrapper>
          <CaseDetailsContent style={{ minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SuccessContainer>
                <IconContainer $status="error">
                    <FiAlertCircle />
                </IconContainer>
                <Title>{t("dueDiligence.paymentSuccess.error.title")}</Title>
                <Message>{error}</Message>
                <Button onClick={() => navigate(caseAlias ? `/due-diligence/case/${caseAlias}` : "/due-diligence")}>
                   {t("dueDiligence.paymentSuccess.error.backButton")}
                </Button>
            </SuccessContainer>
          </CaseDetailsContent>
        </CaseDetailsWrapper>
     );
  }

  return (
    <CaseDetailsWrapper>
      <CaseDetailsContent style={{ minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <SuccessContainer>
            <SuccessCard>
                <IconContainer $status="success">
                    <FiCheckCircle />
                </IconContainer>
                <Title>{t("dueDiligence.paymentSuccess.success.title")}</Title>
                <MessageGroup>
                  <Message>
                    {t("dueDiligence.paymentSuccess.success.message")}
                  </Message>
                  <Message>
                    {t("dueDiligence.paymentSuccess.success.documentsNote")}
                  </Message>
                </MessageGroup>
                <Button onClick={() => navigate(caseAlias ? `/due-diligence/case/${caseAlias}#upload-section` : "/due-diligence")}>
                  {t("dueDiligence.paymentSuccess.success.uploadButton")}
                  <FiArrowRight />
                </Button>
                <SecondaryButton onClick={() => navigate("/due-diligence")}>
                    {t("dueDiligence.paymentSuccess.success.backToListButton")}
                </SecondaryButton>
            </SuccessCard>
        </SuccessContainer>
      </CaseDetailsContent>
    </CaseDetailsWrapper>
  );
};
